import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {TeammateAvatar} from '../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  createFormFieldHoverStyle,
  createFormInputFieldHeight,
} from '../../accounts/createAccount/createAccountStyles';
import {SearchTeammateDropdownContainer} from '../dropdowns/search/searchTeammateDropdownContainer';
import {CreateFormInputDisplay} from './createFormInputDisplay';
import {addCreateFormInputDisabledStyles} from './createFormInputHelpers';

/*
 * Constants.
 */

const popoverButtonWidth = 175; // px

/*
 * Props.
 */

interface TeammatePopoverProps {
  title: ReactNode;
  teammate: TeammateModel | undefined;
  onTeammateSelect: (model: TeammateModel) => void;
  onDelete?: () => void;
  isModalInitiallyOpen?: boolean;
  shouldShowDeleteOptionInDropdown?: boolean;
  isDisabled?: boolean;
}

/*
 * Style.
 */

interface StyledButtonDivProps {
  $isDisabled?: boolean;
}

const StyledButtonDiv = styled.div<StyledButtonDivProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: ${(p) => p.theme.greys.shade20};
  border-radius: 8px;
  padding: 5px 8px;
  min-width: ${popoverButtonWidth}px;
  width: fit-content;
  height: ${createFormInputFieldHeight}px;

  ${createFormFieldHoverStyle};
  ${(p) => addCreateFormInputDisabledStyles(p.$isDisabled)}
`;

const StyledPlaceholderTextSpan = styled.span`
  color: ${(p) => p.theme.greys.shade60};
  white-space: nowrap;
`;

const StyledTeammateTextSpan = styled.span`
  color: ${(p) => p.theme.greys.shade90};
  white-space: nowrap;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/*
 * Component.
 */

export const TeammatePopover: FC<TeammatePopoverProps> = (props) => {
  const theme = useUserTheme();
  const {
    title,
    teammate,
    shouldShowDeleteOptionInDropdown,
    onTeammateSelect,
    onDelete,
    isModalInitiallyOpen,
    isDisabled,
  } = props;

  return (
    <CreateFormInputDisplay
      title={title}
      input={
        <PopoverCoordinator
          isDisabled={isDisabled}
          renderButton={() => (
            <StyledButtonDiv $isDisabled={isDisabled}>
              {renderTeammateOrPlaceholder(teammate)}
              <Icon name="chevronDownSmall" color={theme.greys.shade60} />
            </StyledButtonDiv>
          )}
          renderModal={(popoverProps) => (
            <SearchTeammateDropdownContainer
              {...popoverProps}
              selectedEmail={teammate?.email}
              onChange={(tm) => onTeammateSelect(tm)}
              onDelete={onDelete}
              shouldShowDeleteOption={shouldShowDeleteOptionInDropdown}
            />
          )}
          isModalInitiallyOpen={Boolean(isModalInitiallyOpen)}
        />
      }
      onDelete={shouldShowDeleteOptionInDropdown ? undefined : onDelete}
    />
  );
};

function renderTeammateOrPlaceholder(teammate: TeammateModel | undefined) {
  if (teammate) {
    return (
      <StyledButtonContentDiv>
        <TeammateAvatar teammate={teammate} size={VisualSizesEnum.SMALL} />
        <StyledTeammateTextSpan>{buildTeammateDisplayName(teammate)}</StyledTeammateTextSpan>
      </StyledButtonContentDiv>
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledButtonContentDiv>
          <Icon name="participantSmall" color={theme.greys.shade60} size={16} />
          <StyledPlaceholderTextSpan>
            <Trans id="create-form-teammate-popover-select-teammate">Select teammate</Trans>
          </StyledPlaceholderTextSpan>
        </StyledButtonContentDiv>
      )}
    </UserThemeConsumer>
  );
}
